import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import definition from '@job-schemas/public-ledger/autopay-card-change-fee.json';
import { jobSchema } from '../../job-schema.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import {
  CARD_CHANGE_FEE_TEXT,
  cardChangeFee,
  cardChangeFeeRows,
  feeCardIntro,
} from './autopay-card-change-fee-logic.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';

/**
 * The PUBLIC_LEDGER team's card when the public ledger keeps refusing nodeStream's top-up of a company's operating account
 * for a card change's public ledger fee (#25 phase 6; round 15: its record's, FEE_KIND CARD). public-ledger/autopay/seed-fee
 * raises it in the nodeStream company once the top-up is still failing an hour after its first try (round 12; a top-up the
 * platform account can't afford never comes here, it waits for the platform top-up card), one open per company and row,
 * and keeps its figures up to date. The change is never cancelled for this: the company's new card stays held and the top-up is tried
 * again every 5 minutes. Staff sort out the error and press Resolved, which tries the top-up once at once.
 * Round 11: the same card, titled "The public ledger refused a change fee top-up" (its own job node), for a change of automatic
 * payment amounts' fee top-up that keeps failing, before its signatures are asked for or at its last signature (FEE_KIND
 * CHANGE / SIGN); its intro says a change of amounts waits. The change's own tries go on meanwhile.
 */
@customElement(NSPublicLedgerAutopayCardChangeFee.is)
export default class NSPublicLedgerAutopayCardChangeFee extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-autopay-card-change-fee';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' } }, icon: 'transaction' });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @state()
  private __busy = false;

  // The server keeps the card's metadata up to date (its figures) and the container doesn't pass its job on: the card follows
  // the job itself, and each update re-renders its buttons (updated() tells the container)
  async connectedCallback() {
    await super.connectedCallback();
    const jobId = this._job?.id;
    if (!jobId || !this.isConnected) return;
    this._subscriptions.subscribeDoc<Job>('job', jobId, (updated) => {
      this._job = updated;
      this.requestUpdate();
    });
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __resolved() {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved');
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const fee = cardChangeFee(this._job?.metadata);
    return html`
      <ns-job-layout>
        <p slot="intro">${feeCardIntro(fee)}</p>
        <div class="job-panel">
          <ns-detail-list .rows=${cardChangeFeeRows(fee)}></ns-detail-list>
          <p>${CARD_CHANGE_FEE_TEXT}</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__resolved()}>
        Resolved
      </wa-button>
    `;
  }
}
